import React from 'react';
import { DollarSign, CreditCard, TrendingUp, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';


export default function Billing() {
  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">

      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#9B1421]/20 to-[#7A0F1A]/10 py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="inline-block bg-[#9B1421]/20 p-4 rounded-full mb-6">
              <DollarSign className="w-12 h-12 text-rollops-accent mx-auto" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Billing That <span className="text-rollops-accent">Pays for Itself</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#A9A3A3] mb-8 max-w-2xl mx-auto">
              Zero processing fees. Automated billing. More time to focus on your members.
            </p>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                We know how it feels. You&apos;re spending hours every month chasing down payments, 
                manually processing credit cards, and trying to figure out who owes what. Meanwhile, 
                processing fees are eating into your already tight margins.
              </p>
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                That&apos;s why we built RollOps billing differently. We don&apos;t charge processing fees - 
                you only pay Stripe&apos;s standard rates. Payments go directly to your account. 
                And everything is automated, so you can stop worrying about billing and start focusing on your gym.
              </p>
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                Our zero-fee processing means more money stays in your pocket. For a gym processing 
                $10,000/month, that&apos;s hundreds of dollars saved every year - money that can go back 
                into your business, your members, or your family.
              </p>
            </div>
          </div>
        </section>

        {/* Zero Fee Highlight */}
        <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-[#9B1421]/20 to-[#7A0F1A]/10">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-[#221F1F] border-2 border-[#9B1421] rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Zero Credit Card Processing Fees
              </h2>
              <p className="text-xl md:text-2xl text-[#A9A3A3] mb-6">
                You get <strong className="text-[#EBFFEE]">100% of your subscription price</strong> - zero processing fees.
              </p>
              <p className="text-lg md:text-xl text-[#A9A3A3] mb-4">
                <strong className="text-[#EBFFEE]">$100 membership = $100 in the bank.</strong> Terms and conditions apply.
              </p>
              <p className="text-base md:text-lg text-[#A9A3A3] mb-8">
                Legally and safely pass credit card fees onto end customers without abruptly changing their bill.
              </p>
              <Link
                to="/terms#zero-fee"
                className="inline-block text-rollops-accent hover:text-[#EBFFEE] underline font-semibold"
              >
                Read the full terms →
              </Link>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 md:py-20 px-4 bg-[#1A1818]">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Automated Billing That Just Works
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Automated Recurring Billing</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Set up subscriptions once, and RollOps handles the rest. Members are automatically 
                      charged on their billing date. No more manual processing, no more missed payments.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Real-Time Revenue Tracking</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      See your revenue at a glance. Track monthly recurring revenue, one-time payments, 
                      and identify trends. Make data-driven decisions about your business.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Zap className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Instant Payment Processing</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Payments are processed instantly through Stripe Connect. Funds go directly to your 
                      Stripe account - we never touch your money. Faster payouts, more control.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Flexible Subscription Plans</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Create unlimited subscription plans - monthly, annual, punch cards, or custom plans. 
                      Set up discounts, trial periods, and promotional pricing. Your business, your rules.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Start Saving on Processing Fees Today
            </h2>
            <p className="text-xl text-[#A9A3A3] mb-8">
              Join the Founders Promo and lock in $20/month. Zero processing fees included.
            </p>
            <Link
              to="/founders-promo"
              className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Get Started Now
            </Link>
          </div>
        </section>
      </main>

    </div>
  );
}
